<template>
    <div class="card">
        <header class="card-head">
            <div>
                <div class="card-title">{{ payload.title }}</div>
                <div v-if="payload.subtitle" class="card-sub">{{ payload.subtitle }}</div>
            </div>
            <div v-if="hasBase" class="legend">
                <span class="legend-item">
                    <i class="swatch swatch--current" />Доля {{ periodLabels.current }}
                </span>
                <span class="legend-item">
                    <i class="swatch swatch--base" />Доля {{ periodLabels.base }}
                </span>
            </div>
        </header>

        <div class="table-wrap">
            <table class="tbl">
                <thead>
                    <tr>
                        <th class="col-name">Категория</th>
                        <th class="col-count">Количество</th>
                        <th v-if="hasShares" class="col-share">
                            {{ hasBase ? 'Доли периодов' : `Доля ${periodLabels.current}` }}
                        </th>
                        <th v-if="hasDelta" class="col-delta">Динамика</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="row in visibleRows" :key="row.name">
                        <td class="col-name" :title="row.name">{{ row.name }}</td>

                        <td class="col-count">
                            <div class="count">
                                <div class="bar">
                                    <div class="bar-fill" :style="{ width: row.barPct + '%' }" />
                                </div>
                                <span class="count-value">{{ row.formattedValue }}</span>
                            </div>
                        </td>

                        <td v-if="hasShares" class="col-share">
                            <!-- Полоса из двух долей: длины сегментов пропорциональны
                                 самим долям, поэтому видно, какой период весомее. -->
                            <div class="share-bar">
                                <div
                                    v-if="row.shareCurrent != null"
                                    class="share-seg share-seg--current"
                                    :style="{ width: row.currentWidth + '%' }"
                                >
                                    <span v-if="row.currentWidth >= 18">{{ formatShare(row.shareCurrent) }}</span>
                                </div>
                                <div
                                    v-if="row.shareBase != null"
                                    class="share-seg share-seg--base"
                                    :style="{ width: row.baseWidth + '%' }"
                                >
                                    <span v-if="row.baseWidth >= 18">{{ formatShare(row.shareBase) }}</span>
                                </div>
                            </div>
                            <div v-if="row.needsShareCaption" class="share-caption">
                                <span>{{ formatShare(row.shareCurrent) }}</span>
                                <span v-if="row.shareBase != null" class="muted">
                                    / {{ formatShare(row.shareBase) }}
                                </span>
                            </div>
                        </td>

                        <td v-if="hasDelta" class="col-delta">
                            <div class="delta">
                                <div class="delta-track">
                                    <div
                                        class="delta-fill"
                                        :class="`delta-fill--${row.tone}`"
                                        :style="{ width: row.deltaPct + '%' }"
                                    />
                                </div>
                                <span class="delta-label" :class="'tone-' + row.tone">
                                    {{ row.delta || '—' }}
                                </span>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <button
            v-if="rows.length > collapseLimit"
            type="button"
            class="toggle"
            @click="expanded = !expanded"
        >
            {{ expanded ? 'Свернуть' : `Показать ещё ${rows.length - collapseLimit}` }}
        </button>

        <div v-if="payload.footer" class="footer">{{ payload.footer }}</div>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue'

const props = defineProps({
    payload: { type: Object, required: true },
})

const expanded = ref(false)
const collapseLimit = 12

const periodLabels = computed(() => ({
    current: props.payload?.periodLabels?.current || 'Текущий',
    base: props.payload?.periodLabels?.base || 'База',
}))

function parseNumeric(value) {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null
    if (value == null) return null
    const cleaned = String(value).replace(/[^\d.,-]/g, '').replace(/\s+/g, '').replace(/,/g, '.')
    const n = Number(cleaned)
    return Number.isFinite(n) ? n : null
}

function formatNumber(n) {
    if (n == null) return '—'
    return n.toLocaleString('ru-RU')
}

function formatShare(share) {
    if (typeof share !== 'number' || !Number.isFinite(share)) return '—'
    return `${share.toLocaleString('ru-RU', { maximumFractionDigits: 1 })}%`
}

const rows = computed(() => {
    const raw = Array.isArray(props.payload?.rows) ? props.payload.rows : []
    const parsed = raw.map(row => ({
        name: row?.name || '—',
        numeric: parseNumeric(row?.value),
        shareCurrent: typeof row?.shareCurrent === 'number' ? row.shareCurrent : null,
        shareBase: typeof row?.shareBase === 'number' ? row.shareBase : null,
        delta: typeof row?.delta === 'string' ? row.delta.trim() : '',
        tone: row?.tone,
        explicitBarPct: typeof row?.barPct === 'number' ? row.barPct : null,
    }))

    // Бары считаем от максимума сами: barPct от бэкенда — подсказка, но данные
    // могут прийти и из истории сообщений, где его ещё не было.
    const peak = parsed.reduce((max, row) => (row.numeric != null && row.numeric > max ? row.numeric : max), 0)
    const deltaPeak = parsed.reduce((max, row) => {
        const value = Math.abs(parseNumeric(row.delta) ?? 0)
        return value > max ? value : max
    }, 0)

    return parsed.map(row => {
        let pct = row.explicitBarPct
        if (peak > 0 && row.numeric != null) pct = (row.numeric / peak) * 100
        const tone = ['pos', 'neg', 'neu'].includes(row.tone)
            ? row.tone
            : (row.delta.startsWith('-') ? 'neg' : (row.delta.startsWith('+') ? 'pos' : 'neu'))

        // Пара долей делит одну полосу пропорционально своим значениям — так
        // строка читается как «было / стало» без второй шкалы.
        const shareSum = (row.shareCurrent ?? 0) + (row.shareBase ?? 0)
        const currentWidth = shareSum > 0 ? Math.round(((row.shareCurrent ?? 0) / shareSum) * 100) : 0
        const deltaValue = Math.abs(parseNumeric(row.delta) ?? 0)

        return {
            ...row,
            tone,
            formattedValue: formatNumber(row.numeric),
            barPct: Math.max(2, Math.min(100, Math.round(pct || 0))),
            currentWidth,
            baseWidth: row.shareBase != null ? 100 - currentWidth : 0,
            // Подписи внутри узких сегментов не читаются — выносим их под полосу.
            needsShareCaption: shareSum > 0 && currentWidth < 18 && (100 - currentWidth) < 18,
            deltaPct: deltaPeak > 0 ? Math.max(2, Math.round((deltaValue / deltaPeak) * 100)) : 0,
        }
    })
})

const visibleRows = computed(() => (expanded.value ? rows.value : rows.value.slice(0, collapseLimit)))
const hasBase = computed(() => rows.value.some(row => row.shareBase != null))
const hasShares = computed(() => rows.value.some(row => row.shareCurrent != null || row.shareBase != null))
const hasDelta = computed(() => rows.value.some(row => row.delta))
</script>

<style scoped>
.card {
    background: white;
    border: 1px solid #e0e0e0;
    border-radius: 12px;
    padding: 18px 20px;
    margin: 12px 0;
}

.card-head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 12px;
}

.card-title {
    font-size: 14px;
    font-weight: 700;
    color: #23282C;
    line-height: 1.3;
}

.card-sub {
    font-size: 11px;
    color: #888888;
    margin-top: 4px;
}

.legend {
    display: inline-flex;
    align-items: center;
    gap: 12px;
    font-size: 10.5px;
    color: #4F5256;
    white-space: nowrap;
}

.legend-item {
    display: inline-flex;
    align-items: center;
    gap: 5px;
}

.swatch {
    width: 9px;
    height: 9px;
    border-radius: 2px;
    display: inline-block;
}

/*
    Цвета фирменные, по одному смыслу на оттенок: бирюзовый — текущий период
    (им же залит бар количества), янтарный — база, зелёный и красный остаются
    только за динамикой, чтобы столбец роста нельзя было спутать с объёмом.
*/
.swatch--current { background: #198FA0; }
.swatch--base { background: #F5A623; }

.table-wrap {
    width: 100%;
    overflow-x: auto;
}

.tbl {
    width: 100%;
    border-collapse: collapse;
    font-size: 12.5px;
}

.tbl th {
    text-align: left;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #888888;
    padding: 8px 10px 8px 0;
    border-bottom: 1px solid #e0e0e0;
    white-space: nowrap;
}

.tbl td {
    padding: 9px 10px 9px 0;
    border-bottom: 1px solid #f0f0f0;
    color: #23282C;
    vertical-align: middle;
}

.tbl tr:last-child td { border-bottom: none; }

.col-name {
    max-width: 190px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 600;
}

.col-count { width: 26%; min-width: 120px; }
.col-share { width: 34%; min-width: 150px; }
.col-delta { width: 22%; min-width: 110px; }

.count {
    display: flex;
    align-items: center;
    gap: 8px;
}

.bar {
    flex: 1;
    height: 9px;
    background: #f2f2f2;
    border-radius: 5px;
    overflow: hidden;
}

.bar-fill {
    height: 100%;
    border-radius: 5px;
    background: linear-gradient(90deg, #35A7B6, #198FA0);
    transition: width 0.8s cubic-bezier(.2,.7,.2,1);
}

.count-value {
    font-variant-numeric: tabular-nums;
    font-weight: 600;
    white-space: nowrap;
}

.share-bar {
    display: flex;
    height: 16px;
    border-radius: 4px;
    overflow: hidden;
    background: #f2f2f2;
}

.share-seg {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: 600;
    color: white;
    white-space: nowrap;
    transition: width 0.8s cubic-bezier(.2,.7,.2,1);
}

.share-seg--current { background: #198FA0; }

/* На янтарном белая подпись не читается — там текст графитовый. */
.share-seg--base { background: #F5A623; color: #23282C; }

.share-caption {
    display: flex;
    gap: 4px;
    margin-top: 3px;
    font-size: 10.5px;
    font-variant-numeric: tabular-nums;
}

.delta {
    display: flex;
    align-items: center;
    gap: 8px;
}

.delta-track {
    flex: 1;
    height: 9px;
    background: #f2f2f2;
    border-radius: 5px;
    overflow: hidden;
}

.delta-fill {
    height: 100%;
    border-radius: 5px;
    transition: width 0.8s cubic-bezier(.2,.7,.2,1);
}

.delta-fill--pos { background: #0F6E56; }
.delta-fill--neg { background: #E30613; }
.delta-fill--neu { background: #d6d5d1; }

.delta-label {
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    min-width: 52px;
    text-align: right;
}

.muted { color: #888888; }
.tone-pos { color: #0F6E56; font-weight: 600; }
.tone-neg { color: #E30613; font-weight: 600; }
.tone-neu { color: #888888; }

.toggle {
    margin-top: 10px;
    background: transparent;
    border: 1px solid #e0e0e0;
    padding: 7px 14px;
    border-radius: 6px;
    font-family: inherit;
    font-size: 12px;
    color: #23282C;
    cursor: pointer;
}

.toggle:hover { background: #f0f0f0; border-color: #c0c0c0; }

.footer {
    font-size: 10.5px;
    color: #888888;
    margin-top: 12px;
    padding-top: 10px;
    border-top: 1px dashed #e7e7e7;
}

@media (max-width: 640px) {
    /* На узком экране бар количества уступает место цифрам и долям. */
    .count .bar { display: none; }
    .col-name { max-width: 110px; }
    .delta-track { display: none; }
}
</style>
